<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';

import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';
import EditNameDialog from 'dashboard/components-next/captain/pageComponents/assistant/EditNameDialog.vue';

const emit = defineEmits(['close', 'createAssistant']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const assistants = useMapGetter('captainAssistants/getRecords');

const currentAssistantId = computed(() => route.params.assistantId);

const isAssistantActive = assistant => {
  return assistant.id === Number(currentAssistantId.value);
};

const fetchDataForRoute = async (routeName, assistantId) => {
  const dataFetchMap = {
    captain_assistants_responses_index: async () => {
      await store.dispatch('captainResponses/get', { assistantId });
    },
    captain_assistants_playground_index: () => {
      // Playground doesn't need pre-fetching, it loads on interaction
    },
    captain_assistants_inboxes_index: async () => {
      await store.dispatch('captainInboxes/get', { assistantId });
    },
    captain_tools_index: async () => {
      await store.dispatch('captainCustomTools/get', { page: 1 });
    },
  };

  const fetchFn = dataFetchMap[routeName];
  if (fetchFn) {
    await fetchFn();
  }
};

const handleAssistantChange = async assistant => {
  if (isAssistantActive(assistant)) return;

  const currentRouteName = route.name;
  const targetRouteName =
    currentRouteName || 'captain_assistants_responses_index';

  await fetchDataForRoute(targetRouteName, assistant.id);

  await router.push({
    name: targetRouteName,
    params: {
      accountId: route.params.accountId,
      assistantId: assistant.id,
    },
  });

  emit('close');
};

const openCreateAssistantDialog = () => {
  emit('createAssistant');
  emit('close');
};

const deleteDialogRefs = ref({});

const setDeleteDialogRef = (el, assistantId) => {
  if (el) deleteDialogRefs.value[assistantId] = el;
};

const editNameDialogRefs = ref({});

const setEditNameDialogRef = (el, assistantId) => {
  if (el) editNameDialogRefs.value[assistantId] = el;
};

const handleEditNameClick = (event, assistant) => {
  event.stopPropagation();
  editNameDialogRefs.value[assistant.id]?.dialogRef.open();
};

const handleDeleteClick = (event, assistant) => {
  event.stopPropagation();

  const wasActive = isAssistantActive(assistant);
  const deletedAssistantId = assistant.id;

  if (wasActive) {
    // The `v-for` row hosting the DeleteDialog (and the dialog itself) is
    // torn down by `v-if="assistants.length > 0"` the instant the store
    // removes the assistant — before the dialog's own `deleteSuccess`
    // event can reach this component's listener. Watching the store
    // directly survives that teardown, since the watcher lives on this
    // (still-mounted) component, not on the row being removed.
    const stopWatch = watch(
      assistants,
      currentAssistants => {
        const stillExists = currentAssistants.some(
          remaining => remaining.id === deletedAssistantId
        );
        if (stillExists) return;

        stopWatch();

        const remainingAssistants = currentAssistants;
        if (remainingAssistants.length > 0) {
          router.push({
            name: route.name,
            params: {
              ...route.params,
              assistantId: remainingAssistants[0].id,
            },
          });
        } else {
          router.push({
            name: 'captain_assistants_create_index',
            params: { accountId: route.params.accountId },
          });
        }
      },
      { deep: true }
    );
  }

  deleteDialogRefs.value[assistant.id]?.dialogRef.open();
};

const handleDeleteSuccess = () => {
  emit('close');
};
</script>

<template>
  <div
    class="pt-5 pb-3 bg-n-alpha-3 backdrop-blur-[100px] outline outline-n-container outline-1 z-50 absolute w-[27.5rem] rounded-xl shadow-md flex flex-col gap-4"
  >
    <div
      class="flex items-center justify-between gap-4 px-6 pb-3 border-b border-n-alpha-2"
    >
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <h2
            class="text-base font-medium cursor-pointer text-n-slate-12 w-fit hover:underline"
          >
            {{ t('CAPTAIN.ASSISTANT_SWITCHER.ASSISTANTS') }}
          </h2>
        </div>
        <p class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANT_SWITCHER.SWITCH_ASSISTANT') }}
        </p>
      </div>
      <Button
        :label="t('CAPTAIN.ASSISTANT_SWITCHER.NEW_ASSISTANT')"
        color="slate"
        icon="i-lucide-plus"
        size="sm"
        class="!bg-n-alpha-2 hover:!bg-n-alpha-3"
        @click="openCreateAssistantDialog"
      />
    </div>
    <div v-if="assistants.length > 0" class="flex flex-col gap-2 px-4">
      <div
        v-for="assistant in assistants"
        :key="assistant.id"
        class="flex items-center gap-1"
      >
        <Button
          :label="assistant.name"
          variant="ghost"
          color="slate"
          trailing-icon
          :icon="isAssistantActive(assistant) ? 'i-lucide-check' : ''"
          class="flex-1 !justify-end !px-2 !py-2 hover:!bg-n-alpha-2 [&>.i-lucide-check]:text-n-teal-10 h-9"
          size="sm"
          @click="handleAssistantChange(assistant)"
        >
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ assistant.name || '' }}
          </span>
          <Avatar
            v-if="assistant"
            :name="assistant.name"
            :size="20"
            icon-name="i-lucide-bot"
            rounded-full
          />
        </Button>
        <Button
          icon="i-lucide-pencil-line"
          variant="ghost"
          color="slate"
          size="sm"
          class="!px-2 !py-2 h-9"
          @click="event => handleEditNameClick(event, assistant)"
        />
        <Button
          icon="i-lucide-trash-2"
          variant="ghost"
          color="ruby"
          size="sm"
          class="!px-2 !py-2 h-9"
          @click="event => handleDeleteClick(event, assistant)"
        />
        <DeleteDialog
          :ref="el => setDeleteDialogRef(el, assistant.id)"
          :entity="assistant"
          type="Assistants"
          translation-key="ASSISTANTS"
          @delete-success="handleDeleteSuccess"
        />
        <EditNameDialog
          :ref="el => setEditNameDialogRef(el, assistant.id)"
          :assistant="assistant"
        />
      </div>
    </div>
    <div v-else class="flex flex-col items-center gap-2 px-4 py-3">
      <p class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANT_SWITCHER.EMPTY_LIST') }}
      </p>
    </div>
  </div>
</template>
